// Accordion.tsx

import React, { useEffect, useRef, useState } from "react";

import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";

const Panel = ({ label, content, activeTab, index, activateTab }: any) => {
  const panelRef: any = useRef(null);
  const [height, setHeight] = useState(0);

  const isActive = activeTab === index;

  useEffect(() => {
    if (panelRef.current) {
      setHeight(isActive ? panelRef.current?.scrollHeight : 0);
    }
  }, [isActive]);

  return (
    <div
      className={`
        group
        relative
        overflow-hidden
        rounded-[22px]
        border
        transition-all
        duration-300

        ${
          isActive
            ? `
              border-[rgba(212,168,74,0.2)]
              bg-[linear-gradient(180deg,rgba(212,168,74,0.06),rgba(255,255,255,0.01))]
            `
            : `
              border-[rgb(var(--border-primary))]
              bg-[rgb(var(--surface-2))]
              hover:border-[rgba(212,168,74,0.12)]
              hover:translate-y-[-2px]
            `
        }
      `}
    >
      {/* Active Glow */}
      {isActive && (
        <div
          className="
            absolute
            top-[-50px]
            right-[-50px]
            h-[160px]
            w-[160px]
            rounded-full
            blur-[70px]
            opacity-20
          "
          style={{
            background:
              "radial-gradient(circle, rgba(212,168,74,0.9), transparent 70%)",
          }}
        />
      )}

      {/* Header */}
      <button
        onClick={() => activateTab(index)}
        className="
          relative
          w-full
          p-2
          md:p-3
          flex
          items-start
          justify-between
          gap-5
          text-left
        "
      >
        <div className="flex items-center gap-4">
          <div>
            <div
              className={`
                h-[38px]
                w-[38px]
                rounded-[12px]
                flex
                items-center
                justify-center
                transition-all
                duration-300

                ${
                  isActive
                    ? `
                      bg-[rgba(212,168,74,0.14)]
                      text-[#d4a84a]
                    `
                    : `
                      bg-[rgb(var(--surface-3))]
                      text-[rgb(var(--text-secondary))]
                    `
                }
              `}
            >
              <BoltOutlinedIcon sx={{ fontSize: 18 }} />
            </div>
          </div>

          <div
            className={`
                card-title
                transition-colors
                duration-200
                ${
                  isActive
                    ? "text-[rgb(var(--text-primary))]"
                    : "text-[rgb(var(--text-secondary))]"
                }
              `}
          >
            {label}
          </div>
        </div>

        {/* Plus */}
        <div>
          <div
            className={`
              h-[34px]
              w-[34px]
              rounded-full
              flex
              items-center
              justify-center
              text-[20px]
              transition-all
              duration-300

              ${
                isActive
                  ? `
                    rotate-45
                    bg-[#d4a84a]
                    text-black
                    shadow-[0_0_20px_rgba(212,168,74,0.35)]
                  `
                  : `
                    bg-[rgb(var(--surface-3))]
                    text-[rgb(var(--text-secondary))]
                  `
              }
            `}
          >
            +
          </div>
        </div>
      </button>

      {/* Content */}
      <div
        style={{
          height: `${height}px`,
          overflow: "hidden",
          transition: "height 0.35s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <div
          ref={panelRef}
          className="
            relative
            z-[2]
            px-5
            md:px-6
            pb-6
          "
        >
          <div
            className="
              border-t
              border-[rgba(255,255,255,0.06)]
              pt-5
              space-y-4
            "
          >
            {content.map((item: any, idx: number) =>
              Array.isArray(item) ? (
                <ul
                  key={idx}
                  className="
                    space-y-3
                    pl-5
                    list-disc
                    text-[14px]
                    leading-[1.9]
                    text-[rgb(var(--text-secondary))]
                  "
                >
                  {item.map((subItem, subIdx) => (
                    <li key={subIdx}>{subItem}</li>
                  ))}
                </ul>
              ) : (
                <p
                  key={idx}
                  className="
                    text-[14px]
                    leading-[2]
                    text-[rgb(var(--text-secondary))]
                  "
                >
                  {item}
                </p>
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Accordion = ({ panels }: any) => {
  const [activeTab, setActiveTab] = useState(0);

  const activateTab = (index: number) => {
    setActiveTab((prev) => (prev === index ? -1 : index));
  };

  if (!panels.length) {
    return (
      <div
        className="
          rounded-[22px]
          border
          border-dashed
          border-[rgb(var(--border-primary))]
          bg-[rgb(var(--surface-2))]
          px-6
          py-14
          text-center
        "
      >
        <div
          className="
            text-[18px]
            font-semibold
            text-[rgb(var(--text-primary))]
          "
        >
          No FAQ Results Found
        </div>

        <div
          className="
            mt-2
            text-[14px]
            text-[rgb(var(--text-secondary))]
          "
        >
          Try searching with different keywords.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {panels.map((panel: any, index: number) => (
        <Panel
          key={index}
          activeTab={activeTab}
          index={index}
          {...panel}
          activateTab={activateTab}
        />
      ))}
    </div>
  );
};

export default Accordion;
